/**
 * Colour photos on the product page (Joshua, 24 Oct 2026; the Snowsky Echo is the model page):
 * 1. The page opens on the loop when photo 1 carries one; otherwise, for a product with colours, on its group photo (all
 *    the colours together), or photo 1 when it has none. A link to one colour (?variant=) opens on that colour's photo,
 *    unless there's a loop. sections/main-product.liquid chooses that photo and snippets/media-gallery.liquid passes it
 *    here as data-a3-open, so the theme's own "open on an in-stock colour" change after load can't move it (19 Oct 2026:
 *    it took the Snowsky Disc off its loop onto a blue photo).
 * 2. Every photo of every colour stays in the thumbnails. Nothing is hidden.
 * 3. Picking a colour shows that colour: its linked photo, otherwise the first photo whose alt text names only that colour.
 *
 * Before 24 Oct (13 Oct, after Zach) picking a colour, and opening the page, hid the other colours' photos: opening on one
 * colour hid 799 of the 1,446 photos of 174 of the 185 colour products (the FiiO M21 and Shanling M5 Ultra showed 2).
 *
 * A photo belongs to a colour when its alt text names that colour (whole words, longest colour first, so "translucent
 * black" isn't counted as "black"). Group shots ("…with the other colours", "all colours", "range") belong to none. Colours
 * the product no longer sells count as other colours when they're a whole part of the alt text between commas
 * ("…stand, copper, front"), so "black, gold-plated sockets close-up" isn't taken for a gold photo. Same rules as the
 * colour-photo linking job and output/bgrade/listing_fixes.py.
 * Set up by snippets/media-gallery.liquid:
 * <media-gallery data-a3-colour-index="0" data-a3-colours='["Black","Silver"]' data-a3-open="<media id>">.
 */
(() => {
  const GROUP = /\b(other|all|both|every|two|three|four|five|six|several|many)\s+(the\s+)?colou?rs?\b|\bcolou?rs\b|\bcolou?r (range|options|choices)\b|\b(line-?up|family|range)\b/i;
  const SAME = { grey: ['gray'], gray: ['grey'], aluminium: ['aluminum'], aluminum: ['aluminium'] };
  const COLOUR_WORDS = new Set(['black', 'white', 'red', 'blue', 'green', 'gold', 'silver', 'copper', 'gunmetal', 'grey', 'gray',
    'pink', 'purple', 'orange', 'yellow', 'brown', 'titanium', 'clear', 'transparent', 'bronze', 'rose gold', 'champagne',
    'navy', 'beige', 'cream', 'natural', 'walnut', 'maple', 'khaki', 'teal', 'cyan', 'sky blue', 'light green', 'dark green',
    'space grey', 'graphite', 'midnight', 'ivory', 'tan', 'olive', 'mint', 'lavender', 'violet', 'crimson', 'smoke']);
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /** True if the photo shows the chosen colour and no other (not a group shot). */
  function onlyChosen(alt, chosen, colours) {
    const text = ` ${(alt || '').toLowerCase()} `;
    if (GROUP.test(text)) return false;
    const mine = [chosen, ...(SAME[chosen] || [])];
    let s = text;
    for (const c of [...new Set([...colours, ...mine])].sort((a, b) => b.length - a.length)) {
      const re = new RegExp(`(^|[^a-z0-9])${esc(c)}(?=[^a-z0-9]|$)`, 'g');
      s = s.replace(re, (m, before) => before + (mine.includes(c) ? '\u0001U' : '\u0001O'));
    }
    if (!s.includes('\u0001U') || s.includes('\u0001O')) return false;
    // A colour the product doesn't sell (any more), named as its own part of the alt text.
    return !text.split(',').map((p) => p.trim()).some((p) => COLOUR_WORDS.has(p) && !mine.includes(p) && !colours.includes(p));
  }

  function chosenColour(gallery, section, variant) {
    const idx = Number(gallery.dataset.a3ColourIndex);
    const selector = section.querySelectorAll('.option-selector')[idx];
    let value = null;
    if (selector) {
      value = selector.dataset.selectorType === 'dropdown'
        ? selector.querySelector('.custom-select__btn')?.textContent
        : selector.querySelector('input:checked')?.value;
    }
    if (!value && variant) value = (variant.options || [])[idx] || variant[`option${idx + 1}`];
    return value ? value.trim().toLowerCase() : null;
  }

  const isFilm = (item) => /video/.test(item.dataset.mediaType || ''); // external_video or video (not the loop over photo 1)
  const altOf = (item) => item.querySelector('img[alt]')?.getAttribute('alt');

  function apply(gallery, section, variant, opening = false) {
    const items = Array.from(gallery.querySelectorAll('.media-viewer__item[data-media-id]'));
    // Always scroll the slider to it: the theme has just scrolled to the colour's photo, and its scroll handler would
    // otherwise make that photo current again.
    const show = (item) => {
      if (!item || item === gallery.currentItem || typeof gallery.customSetActiveMedia !== 'function') return;
      gallery.customSetActiveMedia(item, true);
      // Setting a photo pauses every video (pauseAllMedia): start the loop again when the photo carries one.
      const loop = item.querySelector('video[data-a3-loop]');
      if (loop && loop.getAttribute('src') && !loop.dataset.userPaused) { const p = loop.play(); if (p) p.catch(() => {}); }
    };

    // Opening the page: the photo the page chose (loop, group photo, photo 1, or the ?variant= colour's photo).
    if (opening) {
      if (!picked) show(items.find((item) => item.dataset.mediaId === gallery.dataset.a3Open));
      return;
    }

    let colours;
    try { colours = JSON.parse(gallery.dataset.a3Colours || '[]').map((c) => String(c).toLowerCase()); } catch (e) { return; }
    const chosen = chosenColour(gallery, section, variant);
    if (!chosen || colours.length < 2) return;

    // The customer picked a colour: its linked photo, otherwise its first photo of that colour alone. The theme has
    // usually scrolled to the linked photo already; this covers colours without one, and the loop photo staying in front
    // (19 Oct 2026: the thumbnail changed but the loop photo stayed as the main photo). A colour with no photo of its own
    // (the FiiO M21's Black) leaves the photo as it is.
    const linked = variant && variant.featured_media ? String(variant.featured_media.id) : null;
    const own = (item) => item.dataset.mediaId === linked || (!isFilm(item) && onlyChosen(altOf(item), chosen, colours));
    const current = gallery.currentItem;
    if (current && own(current)) return;
    show(items.find((item) => item.dataset.mediaId === linked) || items.find(own));
  }

  // A colour change before the customer has touched the choices is still the page opening (19 Oct 2026: on a repeat visit
  // the theme's "open on an in-stock colour" change, variant-picker.js applyInStockPreference, landed after load).
  let picked = false;
  // A customer already swiping the photos is left alone too.
  const touched = (evt) => { if (evt.isTrusted && evt.target.closest && evt.target.closest('variant-picker, .option-selector, media-gallery')) picked = true; };
  document.addEventListener('pointerdown', touched, true);
  document.addEventListener('keydown', touched, true);
  document.addEventListener('wheel', touched, { capture: true, passive: true });

  document.addEventListener('on:variant:change', (evt) => {
    const section = evt.target.closest('.shopify-section') || document;
    const gallery = section.querySelector('media-gallery[data-a3-colours]');
    if (gallery) apply(gallery, section, evt.detail && evt.detail.variant, !picked);
  });

  // Clicking the colour that's already selected (the one the page opened on) fires no variant change, so a click on the
  // colour choices shows that colour too.
  document.addEventListener('click', (evt) => {
    const sel = evt.target.closest ? evt.target.closest('.option-selector') : null;
    if (!sel) return;
    const section = sel.closest('.shopify-section') || document;
    const gallery = section.querySelector('media-gallery[data-a3-colours]');
    if (!gallery || section.querySelectorAll('.option-selector')[Number(gallery.dataset.a3ColourIndex)] !== sel) return;
    setTimeout(() => apply(gallery, section, null), 60);
  });

  // When the page opens: keep the photo the page chose (see apply).
  const onLoad = () => {
    document.querySelectorAll('media-gallery[data-a3-colours]').forEach((gallery) => {
      apply(gallery, gallery.closest('.shopify-section') || document, null, true);
    });
  };
  if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad);
})();
